import type { JSX } from 'react';
import { useController } from 'react-hook-form';
import { StringUtils } from 'ts/commons/StringUtils';
import type { DropdownItemProps } from 'ts/components/Dropdown';
import { Dropdown } from 'ts/components/Dropdown';
import { FormGroup } from 'ts/components/Form';
import type { FindingsSummaryInfo } from 'typedefs/FindingsSummaryInfo';

const RULENAME_TRUNCATE_LENGTH = 100;

/** Values provided by the GuidelineFilter component. */
export type GuidelineFilterValues = {
	/** The guideline (e.g. AUTOSAR C++14) or the empty string if not set */
	guideline: string;

	/** The list of rules within the guideline (see above) to filter for (e.g. A8-4-2) */
	guidelineRules: string[];
};

/** Props for GuidelineFilter. */
type GuidelineFilterProps = { findingSummary: FindingsSummaryInfo };

/** Dropdown to allow filtering by a Coding guideline. */
export function GuidelineFilter({ findingSummary }: GuidelineFilterProps): JSX.Element | null {
	const guidelineController = useController<GuidelineFilterValues, 'guideline'>({ name: 'guideline' });
	const guidelineRulesController = useController<GuidelineFilterValues, 'guidelineRules'>({ name: 'guidelineRules' });
	const guidelineNames = findingSummary.guidelineInfos.map(guidelineInfo => guidelineInfo.guidelineName);
	const guideline = guidelineController.field.value;
	if (guideline !== '' && !guidelineNames.includes(guideline)) {
		guidelineNames.push(guideline);
	}
	const guidelines = guidelineNames.map((guidelineName): DropdownItemProps => {
		return {
			key: guidelineName,
			value: guidelineName,
			text: guidelineName
		};
	});
	return (
		<FormGroup grouped as="fieldset">
			<legend>Guideline</legend>
			<Dropdown
				fluid
				clearable
				selection
				className="mb-2"
				placeholder="Choose Guideline..."
				options={guidelines}
				value={guideline}
				data-testid="guidelineChooser"
				onChange={(_, data) => {
					const newGuideline = data.value as string;
					guidelineController.field.onChange(newGuideline);
					guidelineRulesController.field.onChange([]);
				}}
			/>
			{guideline ? (
				<GuidelineRuleFilter
					findingSummary={findingSummary}
					guideline={guideline}
					guidelineRules={guidelineRulesController.field.value}
					onRuleFilterChanged={guidelineRules => guidelineRulesController.field.onChange(guidelineRules)}
				/>
			) : null}
		</FormGroup>
	);
}

/** The props for GuidelineRuleFilter */
type GuidelineRuleFilterProps = {
	findingSummary: FindingsSummaryInfo;
	guideline: string | undefined;
	guidelineRules: string[];
	onRuleFilterChanged: (rules: string[]) => void;
};

function GuidelineRuleFilter({
	findingSummary,
	guideline,
	guidelineRules,
	onRuleFilterChanged
}: GuidelineRuleFilterProps) {
	const guidelineInfo = findingSummary.guidelineInfos.find(info => info.guidelineName === guideline);
	const ruleNames = [
		...new Set([...(guidelineInfo?.ruleInfos.map(ruleInfo => ruleInfo.name) ?? []), ...guidelineRules])
	];
	const ruleOptions = ruleNames.map((ruleName): DropdownItemProps => {
		return {
			key: ruleName,
			value: ruleName,
			text: StringUtils.truncateWithEllipsis(RULENAME_TRUNCATE_LENGTH, ruleName)
		};
	});
	return (
		<Dropdown
			fluid
			multiple
			search
			selection
			options={ruleOptions}
			value={guidelineRules}
			data-testid="guidelineFilter"
			placeholder={guidelineRules.length === 0 ? 'All rules (select to narrow down)' : 'Guideline Rules'}
			className="[&_.ui.label]:font-normal"
			onChange={(_, data) => onRuleFilterChanged(data.value as string[])}
		/>
	);
}
